/**
 * One editable product row — design/PROTOTYPE-SPEC.md §6.
 *
 * ## Why editing is explicit rather than edit-in-place
 *
 * The prototype describes "inline row editing", and the cheapest reading of
 * that is inputs that commit on blur. This does not do that, deliberately.
 *
 * This row carries **unit cost and retail price**. A field that saves on blur
 * turns a mis-click plus a stray keystroke into a silently wrong price on a
 * real product, discovered later as a wrong stock valuation or a wrong sale.
 * There is no undo — the audit trail records the change, but nothing puts the
 * old number back for you. Edit → Save → Cancel costs one click and makes every
 * price change something a person decided to do.
 *
 * ## Archive, not delete
 *
 * Products are archived. A movement whose product row was deleted is an audit
 * trail that cannot answer "what was it", and lot traceability on a medical
 * consumable is the thing this product exists to provide.
 */
import { useState } from 'react';
import type { Classification, ProductRow, UpdateProductInput } from '../lib/api.js';
import { c, field, sans } from '../theme.js';
import { money, penceToPounds, poundsToPence, smallButton } from './format.js';
import { CategoryField } from './CategoryField.js';

interface Props {
  row: ProductRow;
  classifications: Classification[];
  /** Every category already in use, for the category list. */
  categories: string[];
  /** Dragging the row onto another type's group moves the product there. */
  onDragStart?: () => void;
  onDragEnd?: () => void;
  mayEdit: boolean;
  onSave: (id: string, input: UpdateProductInput) => Promise<void>;
  onArchive: (id: string) => Promise<void>;
}

const cell: React.CSSProperties = { padding: '11px 12px', fontSize: 13.5, verticalAlign: 'middle' };
const editField: React.CSSProperties = { ...field, height: 34, fontSize: 13 };

export function ProductRowEditor({
  row,
  classifications,
  categories,
  onDragStart,
  onDragEnd,
  mayEdit,
  onSave,
  onArchive,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState(row.name);
  const [category, setCategory] = useState(row.category ?? '');
  const [classificationId, setClassificationId] = useState(row.classificationId ?? '');
  const [cost, setCost] = useState(penceToPounds(row.unitCostPence));
  const [retail, setRetail] = useState(penceToPounds(row.retailPricePence));
  const [minLevel, setMinLevel] = useState(String(row.minLevel));

  function beginEdit() {
    // Reset from the row, not from whatever a previous cancelled edit left in
    // state — otherwise Cancel then Edit reopens the abandoned draft.
    setName(row.name);
    setCategory(row.category ?? '');
    setClassificationId(row.classificationId ?? '');
    setCost(penceToPounds(row.unitCostPence));
    setRetail(penceToPounds(row.retailPricePence));
    setMinLevel(String(row.minLevel));
    setError(null);
    setEditing(true);
  }

  async function save() {
    const costPence = poundsToPence(cost);
    if (costPence === null) {
      setError('Unit cost must be a number, like 95 or 95.50');
      return;
    }
    // An empty retail price is meaningful — it means "we do not sell this" —
    // so it clears the column rather than failing validation.
    const retailPence = retail.trim() === '' ? null : poundsToPence(retail);
    if (retail.trim() !== '' && retailPence === null) {
      setError('Retail price must be a number, or empty if you do not sell it');
      return;
    }
    const min = Number(minLevel);
    if (!Number.isInteger(min) || min < 0) {
      setError('Reorder level must be a whole number');
      return;
    }
    if (name.trim() === '') {
      setError('A product needs a name');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await onSave(row.id, {
        name: name.trim(),
        category: category.trim() === '' ? null : category.trim(),
        ...(classificationId ? { classificationId } : {}),
        unitCostPence: costPence,
        retailPricePence: retailPence,
        minLevel: min,
      });
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save that change');
    } finally {
      setBusy(false);
    }
  }

  if (!editing) {
    return (
      <tr
        draggable={mayEdit && onDragStart !== undefined}
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = 'move';
          // Firefox will not start a drag without data on it.
          e.dataTransfer.setData('text/plain', row.name);
          onDragStart?.();
        }}
        onDragEnd={onDragEnd}
        title={mayEdit && onDragStart ? 'Drag onto another type to move it' : undefined}
        style={{
          borderTop: `1px solid ${c.border}`,
          cursor: mayEdit && onDragStart ? 'grab' : undefined,
        }}
      >
        <td style={{ ...cell, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <div style={{ fontWeight: 500 }}>{row.name}</div>
          <div style={{ color: c.muted2, fontSize: 12, marginTop: 2 }}>
            {[row.brand, row.category].filter(Boolean).join(' · ') || '—'}
          </div>
        </td>
        <td style={{ ...cell, whiteSpace: 'nowrap' }}>{money(row.unitCostPence)}</td>
        <td style={{ ...cell, whiteSpace: 'nowrap' }}>
          {row.retailPricePence === null ? (
            <span style={{ color: c.muted2 }}>Not sold</span>
          ) : (
            money(row.retailPricePence)
          )}
        </td>
        <td style={{ ...cell, whiteSpace: 'nowrap' }}>{row.minLevel}</td>
        <td style={{ ...cell, whiteSpace: 'nowrap', color: c.muted2 }}>{row.qtyOnHand}</td>
        <td style={{ ...cell, textAlign: 'right', whiteSpace: 'nowrap' }}>
          {mayEdit &&
            (confirmArchive ? (
              <>
                <span style={{ fontSize: 12.5, color: c.muted, marginRight: 8 }}>
                  {row.qtyOnHand > 0 ? `Archive with ${row.qtyOnHand} in stock?` : 'Archive it?'}
                </span>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setBusy(true);
                    void onArchive(row.id)
                      .catch((e: unknown) =>
                        setError(e instanceof Error ? e.message : 'Could not archive that'),
                      )
                      .finally(() => {
                        setBusy(false);
                        setConfirmArchive(false);
                      });
                  }}
                  style={{ ...smallButton, color: c.danger }}
                >
                  Yes, archive
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmArchive(false)}
                  style={{ ...smallButton, border: 'none', color: c.muted, marginLeft: 6 }}
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button type="button" onClick={beginEdit} style={smallButton}>
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmArchive(true)}
                  style={{ ...smallButton, marginLeft: 6, color: c.muted }}
                >
                  Archive
                </button>
              </>
            ))}
          {error && (
            <div role="alert" style={{ color: c.danger, fontSize: 12.5, marginTop: 6 }}>
              {error}
            </div>
          )}
        </td>
      </tr>
    );
  }

  return (
    <tr style={{ borderTop: `1px solid ${c.border}`, background: c.well }}>
      <td style={cell}>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label="Product name"
          style={{ ...editField, width: '100%', marginBottom: 6 }}
        />
        <div style={{ display: 'flex', gap: 6 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <CategoryField
              value={category}
              onChange={setCategory}
              categories={categories}
              style={{ ...editField, width: '100%' }}
            />
          </div>
          <select
            value={classificationId}
            onChange={(e) => setClassificationId(e.target.value)}
            aria-label="Classification"
            style={{ ...editField, flex: 1, fontFamily: sans }}
          >
            {classifications.map((cl) => (
              <option key={cl.id} value={cl.id}>
                {cl.label}
              </option>
            ))}
          </select>
        </div>
      </td>
      <td style={cell}>
        <input
          value={cost}
          onChange={(e) => setCost(e.target.value)}
          inputMode="decimal"
          aria-label="Unit cost in pounds"
          style={{ ...editField, width: 84 }}
        />
      </td>
      <td style={cell}>
        <input
          value={retail}
          onChange={(e) => setRetail(e.target.value)}
          inputMode="decimal"
          placeholder="—"
          aria-label="Retail price in pounds"
          style={{ ...editField, width: 84 }}
        />
      </td>
      <td style={cell}>
        <input
          value={minLevel}
          onChange={(e) => setMinLevel(e.target.value)}
          inputMode="numeric"
          aria-label="Reorder level"
          style={{ ...editField, width: 64 }}
        />
      </td>
      <td style={{ ...cell, color: c.muted2 }}>{row.qtyOnHand}</td>
      <td style={{ ...cell, textAlign: 'right', whiteSpace: 'nowrap' }}>
        <button
          type="button"
          disabled={busy}
          onClick={() => void save()}
          style={{ ...smallButton, opacity: busy ? 0.5 : 1 }}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={() => setEditing(false)}
          style={{ ...smallButton, border: 'none', color: c.muted, marginLeft: 6 }}
        >
          Cancel
        </button>
        {error && (
          <div role="alert" style={{ color: c.danger, fontSize: 12.5, marginTop: 6 }}>
            {error}
          </div>
        )}
      </td>
    </tr>
  );
}
